import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Lock, Mail, ArrowLeft, Shield } from "lucide-react";
import InstallPWAButton from "@/components/InstallPWAButton";

import { logoColor as LOGO_URL } from "@/lib/logos";

export default function TecnicoLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [expiredNotice, setExpiredNotice] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("odf_session_expired")) {
      setExpiredNotice(true);
      localStorage.removeItem("odf_session_expired");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Completa todos los campos"); return; }
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/tecnico/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Credenciales incorrectas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center px-4">
      <Card className="w-full max-w-sm border-0 shadow-xl rounded-2xl">
        <CardContent className="p-8">
          <Link to="/" className="text-sm text-gray-400 hover:text-gray-600 mb-6 flex items-center gap-1">
            <ArrowLeft className="h-3.5 w-3.5" /> Volver al sitio
          </Link>

          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Odontofix" className="h-14 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-[#384091]">Portal Técnico</h1>
            <p className="text-gray-500 text-sm mt-1">Acceso exclusivo para técnicos Odontofix</p>
          </div>

          {expiredNotice && (
            <div className="mb-4 bg-amber-50 text-amber-700 text-sm p-3 rounded-lg">
              Tu sesión se cerró automáticamente por 2 horas de inactividad. Inicia sesión nuevamente.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Correo</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@odontofix.cl" className="pl-10 h-11" autoComplete="email" />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="pl-10 h-11" autoComplete="current-password" />
              </div>
            </div>

            {error && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">{error}</div>}

            <Button type="submit" className="w-full h-11 bg-[#384091] hover:bg-[#2D3375] text-white font-medium" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Iniciar sesión
            </Button>
          </form>

          <div className="mt-4 bg-blue-50 rounded-lg p-3 flex items-start gap-2">
            <Shield className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-blue-600">Contraseñas hasheadas con bcrypt · JWT httpOnly · Datos cifrados en tránsito</p>
          </div>

          <div className="mt-3 flex justify-center">
            <InstallPWAButton />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
